import React, { useState, useEffect } from 'react';
import { string, any, bool, array, object, func } from 'prop-types';
import styled from 'styled-components';
import { Tooltip } from 'antd';
import { get, groupBy, sum, includes, map, last, split, find, size } from 'lodash';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import moment from 'moment';
import { withApollo } from 'react-apollo';
import {
    Text,
    Icon,
    Card,
    Title,
    Subtitle,
    Row,
    Column,
    Divider,
    Box,
    Button,
    FlexContainer,
    ConditionalRender,
    If,
    Truncate,
    OrderShippingEstimate,
    Number,
    MobileWrapper,
    IsMobileWrapper,
    HasPermissions,
    IconContainer,
    AlertLabel,
    Link
} from '../../common';
import { AssignPicker } from '../../graphql';
import { OrderPickerManager, ReceiverInfo } from '../Orders';
import { withReloadable } from '../../../hoc';
import { removedItemsCount, isEqualToZero, secondsToHms, getStatusCode } from '../../../utils/misc';
import ModifyPrice from '../ModifyPrice';
import FormPOS from '../FormPOS';
import { changesOrders } from './../../utils/StatusOrder';
import DownloadInvoice from '../DownloadInvoice';
import DownloadChangeTicket from '../DownloadChangeTicket';
import GET_PAYMENT_CARD_INFO from '../../graphql/getOrderPaymentCardInfo';
import { QUERY_GET_ORDER_QR_BY_NUMORDER } from '../../graphql/getOrderQrById';
import { TIGERS_EYE, BLUE, RED } from '../../../utils/colors';
import OrderQrStatus from './OrderQrStatus'

const pendingPickingValues = getStatusCode('Pendiente picking');
const enPickingValues = getStatusCode('En picking');
const CardDivider = styled.div`
    border-bottom: 1px solid #eaeaea;
    padding-bottom: 6px;
    margin-bottom: 6px;
`;

const StatInfo = ({ value, icon, color }) => (
    <div>
        <Icon
            name={icon}
            style={{ margin: '0px 2px', color, fontSize: '9px' }}
        />
        <span style={{ color }}>{value}</span>
    </div>
);

StatInfo.propTypes = {
    value: string,
    icon: string,
    color: string
};

const BoxHeader = ({ title, subtitle, extra, ...props }) => (
    <div className='box-title-section flex'>
        <div className='title flex-1'>
            <Truncate {...props}>
                <Text
                    text={title}
                    renderAs='h4'
                    style={{ margin: 0, color: '#333333' }}
                    semibold
                />
            </Truncate>
            { subtitle }
        </div>
        <div className='extra flex-1 flex justify-content-flex-end'>
            { extra }
        </div>
    </div>
);

BoxHeader.propTypes = {
    title: string,
    subtitle: string,
    extra: any
};

BoxHeader.defaultProps = {
    title: 'title',
    subtitle: 'subtitle'
};

const DataField = ({ label, value, alignValues = 'flex-start' }) => (
    <Row mY={2}>
        <Column>
            { label }
        </Column>
        <Column align={alignValues}>
            { value }
        </Column>
    </Row>
);

DataField.propTypes = {
    label: any,
    value: any,
    alignValues: string
};

const PickerStatistics = ({
    values = [],
    withPicking,
    ...props
}) => (
    <div className='box-section flex justify-content-space-center' {...props}>
        {values.map(([label, value], key) => (
            <div className='qualHMVWrap eqWrap flex-1' key={key}>
                <div className='equalHMV  flex flex-1 justify-content-center'>
                    <FlexContainer direction='column'>
                        <If
                            condition={typeof value === 'function'}
                            then={value}
                            else={(
                                <>
                                    <span style={{
                                        display: 'block',
                                        fontSize: '.75rem',
                                        color: '#333333',
                                        fontWeight: 'bold',
                                        textAlign: 'center'
                                    }}>
                                        {(!withPicking && value === 0) ? '-' : value}
                                    </span>
                                    <Text text={label} centered tiny />
                                </>
                            )}
                        />
                    </FlexContainer>
                </div>
            </div>
        ))}
    </div>
);


PickerStatistics.propTypes = {
    values: array,
    withPicking: bool
};

const InfoToggler = ({ onClick, opened }) => (
    <MobileWrapper>
        <Column noFlex justify='center'>
            <Icon name={opened ? 'minus' : 'plus'} color='#A4C735' size={14} onClick={onClick} pointer />
        </Column>
    </MobileWrapper>
);

const OrderResume = ({ data, withPicking, inPicking, onClickCallHistory, lastKnownStatusCode, client }) => {

    const [copied, setCopied] = useState(false);
    const [showCustomerInfo, setShowCustomerInfo] = useState(true);
    const [showPickingInfo, setShowPickingInfo] = useState(true);
    const [showTotalInfo, setShowTotalInfo] = useState(true);
    const [showDeliveryInfo, setShowDeliveryInfo] = useState(true);
    const [orderQrData, setOrderQrData] = useState([]);
    const status = get(data, 'assignInfo.status.code', '0');
    const itemsQty = get(data, 'assignInfo.pickedCount', 0);
    const pickedItems = get(data, 'assignInfo.pickedItemCount');
    const totals = groupBy(get(data, 'totals', []), 'id');
    const pickedAmount = get(data, 'assignInfo.pickedAmount', 0);
    const replacedAmount = get(data, 'assignInfo.replacedAmount', 0);
    const replacedWithExcessAmount = get(data, 'assignInfo.replacedWithExcessAmount', 0);
    const noStockAmount = get(data, 'assignInfo.noStockAmount', 0);
    const addedAmount = get(data, 'assignInfo.addedAmount', 0);
    const unpickedAmount = get(data, 'assignInfo.unpickedAmount', 0);

    const boughtTotal = sum([get(data, 'assignInfo.pickedAmount'), get(data, 'assignInfo.replacedAmount'), get(data, 'assignInfo.addedAmount')]);
    const store = get(data, 'storeName', 'Tienda desconocida');
    const salesChannelId = get(data, 'salesChannel.id', '0');
    const foundRate = get(data, 'assignInfo.foundRate', '-');
    const fillRate = get(data, 'assignInfo.fillRate', '-');
    const pickingTimeInit = get(data, 'pickingTime.init');
    const pickingTimeEnd = get(data, 'pickingTime.end');
    const priceTotal = parseInt(get(totals, 'items.0.value')) + parseInt(get(totals, 'discounts.0.value'));
    const posDetail = get(data, 'assignInfo.posDetail.0', []);
    const documentNumber = get(posDetail, 'documentNumber', null);
    const amountInvoice = get(data, 'assignInfo.amountInvoice', 0);
    const reservedAmount = get(data, 'paymentData.transactions.0.payments.0.amount');
    const paymentMethods = map(get(data, 'paymentData.transactions.0.payments', []), 'method')
        .filter(Boolean)
        .join(' + ') || '-';
    const shippingChange = get(data, 'shippingChange', false);
    const weightError = get(data, 'assignInfo.weightError', null);
    const isTraditional = get(data, 'assignInfo.isTraditional', null);
    const semaphore = get(data, 'assignInfo.semaphore', null);
    const isPersonalShopperCustomer = get(data, 'assignInfo.isPersonalShopperCustomer', false);
    const pickedForInvoice = includes(['4'], status);
    const [lastDigits, setLastDigits] = useState(undefined);
    const [brand, setBrand] = useState(undefined);
    const courierDelivery = get(data, 'assignInfo.courierDelivery', null);
    

    useEffect(() => {
        async function getPaymentCardInfo () {
            const response = await client.query({
                query: GET_PAYMENT_CARD_INFO,
                variables: {
                    numOrder: get(data, 'numOrder')
                }
            });

            const card = get(response, 'data.getOrderPaymentInfo.card', {});

            if (lastDigits === undefined && brand === undefined) {
                setLastDigits(get(card, 'lastDigits', '-'));
                setBrand(get(card, 'brand', '-'));
            };
        }
        getPaymentCardInfo();
    }, []);

    useEffect(() => {
        if (weightError !== 'null' && weightError !== 'false') {
            setOrderQrData([]);
            return;
        }

        let cancelled = false;
        client.query({
            query: QUERY_GET_ORDER_QR_BY_NUMORDER,
            variables: { numOrder: get(data, 'id', 0) },
            fetchPolicy: 'network-only'
        }).then(res => {
            if (cancelled) return;
            setOrderQrData(get(res, 'data.getOrderQrById', []));
        }).catch(() => {
            if (!cancelled) setOrderQrData([]);
        });

        return () => { cancelled = true; };
    }, [get(data, 'id', 0), weightError, client]);
    const isOnAudit = includes(['20', '30', '31', '32', '33', '40'], status);

    const subTotalAmount = () => Math.abs(priceTotal) - Math.abs(replacedAmount) - Math.abs(noStockAmount) - Math.abs(removedItemsCount(data));

    const diffAmount = () => {
        const total = reservedAmount - amountInvoice;
        return total > 0 ? <Number value={total} isPrice negative /> : <Number value={Math.abs(total)} isPrice />;
    };

    const ValueFoundRate = () => {
        if (includes(['1', '3', '41', '21'], status)) {
            return (
                <StatInfo
                    value={'-'}
                    color={'#000000'}
                />
            );
        } else {
            return (
                <StatInfo
                    value={(!withPicking && foundRate === 0) || isOnAudit || inPicking  ? '-' : `${foundRate}%`}
                    icon={isEqualToZero(foundRate, foundRate > 93 ? 'arrow-up' : 'arrow-down', null)}
                    color={foundRate > 93 ? '#1fa02e' : '#cc182e'}
                />
            );
        };
    };

    const ValueFillrate = ({ fillRate }) => {
        if(includes(['1', '41', '21'], status)) {
            return (
                <StatInfo
                    value={'-'}
                    color={'#000000'}
                />
            );
        } else {
            return (
                <StatInfo
                    value={(!withPicking && fillRate === 0) || isOnAudit || inPicking ? '-' : `${fillRate}%`}
                    icon={inPicking ? null : isEqualToZero(fillRate, fillRate > 93 ? 'arrow-up' : 'arrow-down', null)}
                    color={inPicking ? '#333333' : fillRate > 93 ? '#1fa02e' : '#cc182e'}
                />
            );
        }
    };

    const pickerStatsA = ({ quantityPicked, itemCount }) => [
        [
            'Items',
            (pendingPickingValues.includes(String(status)) || enPickingValues.includes(String(status))) ?
                <HasPermissions to={['order-detail-page:view-items']} otherwise="-">
                    {`${(!withPicking && itemsQty === 0) || inPicking ? '-' : itemsQty}/${get(data, 'skuCount')}`}
                </HasPermissions>
            :
                `${(!withPicking && itemsQty === 0) || inPicking ? '-' : itemsQty}/${get(data, 'skuCount')}`
        ],
        [
            'Foundrate',
            <ValueFoundRate />
        ],
        [
            'Unidades', 
            (pendingPickingValues.includes(String(status)) || enPickingValues.includes(String(status))) ?
                <HasPermissions to={['order-detail-page:view-units']} otherwise="-">
                    {`${(!withPicking && pickedItems === 0) || inPicking ? '-' : quantityPicked}/${get(data, 'itemCount', 0)}`}
                </HasPermissions>
            :
            `${(!withPicking && pickedItems === 0) || inPicking ? '-' : quantityPicked}/${get(data, 'itemCount', 0)}`
        ],
        [
            'Fillrate',
            <ValueFillrate fillRate={fillRate} />
        ]
    ];

    const pickerStatsB = [
        ['Pickeados', inPicking ? '-' : itemsQty],
        ['Sustituidos', inPicking ? '-' : get(data, 'assignInfo.replacedCount', 0)],
        ['Sin Stock', inPicking ? '-' : get(data, 'assignInfo.noStockCount', 0)],
        ['Agregados', inPicking ? '-' : get(data, 'assignInfo.addedCount', 0)]
    ];

    const pickerStatsC = [
        ['Solicitado', <Number value={priceTotal} />],
        ['Comprado', inPicking ? '-' : (boughtTotal === 0 ? '-' : <Number value={boughtTotal} />)],
        ['% Solicitado', inPicking ? '-' : isEqualToZero(parseInt((boughtTotal / priceTotal) * 100), `${parseInt((boughtTotal / priceTotal) * 100)} %`)]
    ];

    const TruncateTextStyle = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

    const pickerStatsD = [
        [
            'Inicio Picking',
            pickingTimeInit ? moment(pickingTimeInit).format('HH:mm') : '-'
        ],
        [
            'Fin Picking',
            pickingTimeEnd ? moment(pickingTimeEnd).format('HH:mm') : '-'
        ],
        [
            'Facturado',
            get(posDetail, 'hour', '-')
        ]
    ];

    const getIconTypeClient = () => {
        return <div style={{ display: 'inline' }}>
            {get(data, 'customer.isUltraVIP') && <Icon name='ultravip' size={17} right={5} color='#1C8AF5' />}
            {get(data, 'customer.isVIP') && <Icon name='vip' size={20} right={5} color='#4DB457' />}
            {get(data, 'customer.isCencoPrime') && <span style={{ fontSize: 18 }}>
                <span className='icon-ic-prime'>
                    <span className='path1' />
                    <span className='path2' />
                </span>
            </span> }
        </div>;
    };

    const AssignShopper = () => (
        <>
            <AssignPicker>
                {({ updateFn, loading }) => (
                    <Row align='center' gap={5}>
                        <Text strong text='Shopper :' />
                        <OrderPickerManager
                            styles={{ justifyContent: 'flex-start' }}
                            filters={{
                                filters: [
                                    { field: 'salesChannels', value: [salesChannelId] },
                                    { field: 'statusUser', value: ['0'] }
                                ]
                            }}
                            onSelectPicker={({ value }, salesChannelId) => Promise.resolve(
                                updateFn({
                                    variables: {
                                        input: { orderId: get(data, 'id'), userId: value, salesChannelId }
                                    }
                                })
                            )}
                            loading={loading}
                            {...data}
                        />
                    </Row>
                )}
            </AssignPicker>
        </>
    );

    const getFormatDeliveryTime = (deliveryTime, anticipated) => {
        if (anticipated) {
            const splittedDeliveryTime = split(deliveryTime, '-');
            return shippingChange ? deliveryTime : `Antes de las ${last(splittedDeliveryTime)}`;
        }
        return deliveryTime;
    };

    const renderAlertQueuing = (isQueued) => {
        const queuingText = `Demora en la descarga de ${secondsToHms(get(data, 'extIssueDelayedByQueuing'))}`;
        const externalFactorsText = 'Marca de atraso por factor externo';

        const error = {
            msg: isQueued ? queuingText : externalFactorsText,
            color: '#BD2C00',
            icon: 'alert-circle'
        };

        const { msg, icon, color } = error;

        return <Row backgroundColor='#f4f4f4' style={{ borderTop: '1px solid #eaeaea', marginTop: 5 }}>
            <Column direction='row' align='center'>
                <Row align='center' noWrap gap={5} style={{ backgroundColor: '#F7E6E1', border: `1px solid #BD2C00`, borderRadius: 8, padding: '9px 12px' }}>
                    <IconContainer size={30}>
                        <Icon name={icon} size={24} color={color} />
                    </IconContainer>
                    <Text text={msg} size={12} color='#323A44' strong />
                </Row>
            </Column>
        </Row>;
    };

    const getColorAndTextSemaphore = (type) => {
        if (type === 'small') return { color: '#1fa02e', text: 'Baja', backgroundColor: '#E6F5F1' };
        if (type === 'medium') return { color: '#EB963C', text: 'Media', backgroundColor: 'rgb(255, 242, 208)' };
        if (type === 'large') return { color: '#cc2c3b', text: 'Alta', backgroundColor: '#f9e6e8' };
    }

    return (
        <IsMobileWrapper>
            {({ isMobile }) => {
                const boxStyle = isMobile ? { height: 'fit-content' } : {};
                const orderBipicking = get(data, 'assignInfo.ordersBipicking', []);
                const relatedOrderId = find(orderBipicking, (orderId) => orderId !== get(data, 'id'));
                const isBipicking = size(orderBipicking) > 0;
                const semaphoreStyles = getColorAndTextSemaphore(semaphore);

                return (
                    <div style={{ backgroundColor: '#f4f4f4', display: 'flex', flexShrink: '0', flexWrap: 'wrap' }}>
                        <Box width={['100%', '50%', '25%']} style={boxStyle}>
                            <Card
                                style={{ display: 'flex' }}
                                margin={7}
                                noHeaderDivider
                                leftHeader={() => (
                                    <Row>
                                        <Column flex={3} style={{ display: 'grid' }} onMouseEnter={() => setCopied(false)}>
                                            <Title text={
                                                <Row justify='space-between' gap={5}>
                                                    {`${get(data, 'customer.firstName')} ${get(data, 'customer.lastName')}`}
                                                    {getIconTypeClient()}
                                                </Row>
                                            }
                                            />
                                            <HasPermissions to={['order-detail-page:client-data']}>
                                                <Tooltip title={copied ? 'Copiado' : 'Haga click para copiar'}>
                                                    <CopyToClipboard text={`${get(data, 'customer.email')}`} onCopy={() => setCopied(true)}>
                                                        <Subtitle
                                                            style={TruncateTextStyle}
                                                            color='#848484'
                                                            text={`${get(data, 'customer.email')}`}
                                                        />
                                                    </CopyToClipboard>
                                                </Tooltip>
                                            </HasPermissions>
                                        </Column>
                                        <InfoToggler onClick={() => setShowCustomerInfo(!showCustomerInfo)} opened={!showCustomerInfo} />
                                    </Row>
                                )}
                                bodyChildren={() => (
                                    <MobileWrapper condition={!showCustomerInfo} showDesktop>
                                        <CardDivider />
                                        <HasPermissions to={['order-detail-page:client-data']}>
                                            <DataField
                                                label={<Text text='order_detail.receiver_phone' strong />}
                                                value={<Text text={get(data, 'customer.phone')} />}
                                            />
                                            <DataField
                                            label={<Text text='order_detail.receiver_document' strong />}
                                            value={<Text text={get(data, 'customer.document')} />}
                                            />
                                            <DataField
                                                label={<Text text='order_detail.receiver_address' strong />}
                                                value={<Text text={get(data, 'selectedAddress.addressLine')} />}
                                            />
                                        </HasPermissions>
                                        
                                        
                                        <DataField
                                            label={<Text text='Preferencia contacto' strong />}
                                            value={<Text text={get(data, 'contactPreference')} />}
                                        />
                                        <Divider margin={11} />
                                        <HasPermissions to={['order-detail-page:payment-method']}>
                                            <DataField
                                                label={<Text text='order_detail.payment_method' strong />}
                                                value={<Text text={paymentMethods} />}
                                            />
                                        </HasPermissions>
                                        <HasPermissions to={['order-detail-page:brand']}>
                                            <DataField
                                                label={<Text text='Tipo tarjeta' strong />}
                                                value={<Text text={brand} />}
                                            />
                                        </HasPermissions>
                                        <HasPermissions to={['order-detail-page:last-digits']}>
                                            <DataField
                                                label={<Text text='order_detail.card_number' strong />}
                                                value={<Text text={lastDigits} />}
                                            />
                                        </HasPermissions>
                                        <DataField
                                            label={<Text text='Monto del pedido' strong />}
                                            value={<Text text={reservedAmount <= 0 ? '-' : <Number value={reservedAmount} style={{ fontSize: '12px' }} />} />}
                                        />
                                        <DataField
                                            label={<Text text='Cambios del pedido' strong />}
                                            value={<Text text={changesOrders(status) & !includes(['4', '6', '7'], lastKnownStatusCode) ? diffAmount() : '-'} />}
                                            // value={<Text text={withPicking ? diffAmount() : '-'} />}
                                        />
                                        <DataField
                                            label={<Text text='order_detail.made_from' strong />}
                                            value={<Text text={get(data, 'origin', 'Web')} />}
                                        />
                                        <DataField
                                            label={<Text text='Cuotas' strong />}
                                            value={<Text text={get(data, 'paymentData.transactions.0.payments.0.installments')} />}
                                        />
                                        <HasPermissions to={['order-detail-page:authorization-code']}>
                                            <DataField
                                                label={<Text text='Código autorización' strong />}
                                                value={<Text text={get(data, 'paymentData.transactions.0.payments.0.authCode')} />}
                                            />
                                        </HasPermissions>
                                    </MobileWrapper>
                                )}
                            />
                        </Box>
                        <Box width={['100%', '50%', '25%']} direction='column' style={boxStyle}>
                            <Card
                                style={{ display: 'flex' }}
                                top={7}
                                bottom={7}
                                noHeaderDivider
                                leftHeader={() => (
                                    <FlexContainer flex={1} direction='column'>
                                        <Row>
                                            <Column flex={2}>
                                                <Title text='Picking' />
                                            </Column>
                                            <InfoToggler onClick={() => setShowPickingInfo(!showPickingInfo)} opened={!showPickingInfo} />
                                        </Row>
                                        <Row>
                                            <MobileWrapper>
                                                <AssignShopper />
                                            </MobileWrapper>
                                        </Row>
                                    </FlexContainer>
                                )}
                                rightHeader={() => (
                                    <>
                                        {isBipicking && 
                                            <FlexContainer direction='column'>
                                                <Row>
                                                    <Icon name='bipicking-a' size={18} color={BLUE} />
                                                    <Icon name='bipicking-b' size={18} color={RED} style={{ margin: '0px 6px 0px -4px' }}/>
                                                    <Column flex={2}>
                                                        <Text link text={`Emparejada con: `}  />
                                                        <Link text={relatedOrderId} src={`/orders/${relatedOrderId}`} bold/>
                                                    </Column>
                                                </Row>
                                            </FlexContainer>
                                        }
                                        {isPersonalShopperCustomer && <Icon name='human-star' size={22} color='#C65006' />}
                                    </>
                                )}
                                bodyChildren={() => (
                                    <MobileWrapper condition={!showPickingInfo} showDesktop>
                                        <CardDivider />
                                        <PickerStatistics
                                            withPicking={withPicking}
                                            values={pickerStatsA(get(data, 'assignInfo.pickingStats', {}))}
                                            style={{ marginBottom: '5px' }}
                                        />
                                        <PickerStatistics withPicking={withPicking} values={pickerStatsB} />
                                        <Divider margin={5} />
                                        <PickerStatistics withPicking={withPicking} values={pickerStatsC} style={{ marginBottom: '5px' }} />
                                        <PickerStatistics withPicking={withPicking} values={pickerStatsD} />
                                        <MobileWrapper hide showDesktop>
                                            <Divider style={{ padding: 0, marginBottom: 5 }} />
                                            <AssignShopper />
                                            <Button
                                                height={18}
                                                padding={'0px'}
                                                width='250px'
                                                link
                                                text='Registrar llamado'
                                                onClick={() => onClickCallHistory('call_history')}
                                            />
                                        </MobileWrapper>
                                    </MobileWrapper>
                                )}
                            />
                            {(isTraditional === 'true' || weightError === 'true' || weightError === 'false' || (weightError === 'null' && orderQrData.length > 0 && get(last(orderQrData), 'actionNumber', 0) > 0)) && (
                                <Card
                                    style={{ display: 'flex' }}
                                    bottom={7}
                                    noHeaderDivider
                                    bodyChildren={() => (
                                        <MobileWrapper showDesktop>
                                            {isTraditional === 'true' && (<Row gap={5} align='center'>
                                                <Text strong text={`Validación de peso: `} />
                                                <AlertLabel type='medium' text='Sin peso' width='auto' iconName='minus' iconSize={8} borderIconContainer='1px solid #EB963C' backgroundIconContainer='rgb(255,242,208)' />
                                            </Row>)}
                                            {(weightError === 'true' || weightError === 'false') && <>
                                                <FlexContainer direction='column'>
                                                    <Text strong text={`Validación de peso: `}/>
                                                    <Row gap={16} align='center'>
                                                        {weightError === 'true' && (
                                                            <AlertLabel type='error' text='Rechazada' width='130px' />
                                                        )}

                                                        {weightError === 'false' && (
                                                            <AlertLabel type='success' text='Aprobada' width='130px' />
                                                        )}

                                                        {semaphore && <Row gap={5}>
                                                            <Text text={'Desviación:'} />
                                                            <Text  text={semaphoreStyles.text} color={semaphoreStyles.color} size={12} />
                                                        </Row>}
                                                    </Row>
                                                </FlexContainer>
                                            </>}
                                            {(weightError === 'false' || weightError === 'null') &&
                                                <OrderQrStatus orderQrData={orderQrData} id={get(data, 'id')} storeName={store} isBipicking={isBipicking} relatedOrderId={relatedOrderId} />
                                            }
                                        </MobileWrapper>
                                    )}
                                />
                            )}
                        </Box>
                        <Box width={['100%', '50%', '25%']} style={boxStyle}>
                            <Card
                                margin={7}
                                noHeaderDivider
                                leftHeader={() => (
                                    <Row >
                                        <Column style={{ minHeight: '40px' }}>
                                            <Title text='Total Compras' />
                                            { documentNumber && (
                                            <Subtitle
                                                style={TruncateTextStyle}
                                                color='#848484'
                                                text={`Boleta | ${documentNumber}`}
                                            />
                                            )}
                                        </Column>
                                        <InfoToggler onClick={() => setShowTotalInfo(!showTotalInfo)} opened={!showTotalInfo} />
                                    </Row>
                                )}
                                bodyChildren={() => (
                                    <MobileWrapper condition={!showTotalInfo} showDesktop>
                                        <CardDivider />
                                        <DataField
                                            alignValues='flex-end'
                                            label={<Text strong text='Sub total' />}
                                            value={<Text text={<Number value={`${get(totals, 'items.0.value')}`} />} />}
                                        />
                                        <DataField
                                            alignValues='flex-end'
                                            label={<Text strong text='Descuentos' />}
                                            value={<Text text={<Number value={`${get(totals, 'discounts.0.value')}`} />} />}
                                        />
                                        <DataField
                                            alignValues='flex-end'
                                            label={<Text strong text='Total pedido' />}
                                            value={<Text strong text={<Number value={`${priceTotal}`} />} />}
                                        />
                                        <Divider margin={5} />
                                        <ConditionalRender condition={withPicking}>
                                            <DataField
                                                alignValues='flex-end'
                                                label={<Text strong text='Pickeados' />}
                                                value={<Text text={<Number value={pickedAmount} />} />}
                                            />
                                            <DataField
                                                alignValues='flex-end'
                                                label={<Text strong text='Descuentos sustitutos' />}
                                                value={
                                                    <Row>
                                                        { replacedAmount !== 0 && <Text text='-' /> }
                                                        <Text text={<Number value={replacedAmount} />} />
                                                    </Row>
                                                }
                                            />
                                            <DataField
                                                alignValues='flex-end'
                                                label={<Text strong text='Exceso de sustitutos' />}
                                                value={<Text text={<Number value={replacedWithExcessAmount} />} />}
                                            />
                                            <DataField
                                                alignValues='flex-end'
                                                label={<Text strong text='Sin stock' />}
                                                value={
                                                    <Row>
                                                        { noStockAmount !== 0 && <Text text='-' /> }
                                                        <Text text={<Number value={noStockAmount} />} />
                                                    </Row>
                                                }
                                            />
                                            <DataField
                                                alignValues='flex-end'
                                                label={<Text strong text='Agregados' />}
                                                value={<Text text={<Number value={addedAmount} />} />}
                                            />
                                            <DataField
                                                alignValues='flex-end'
                                                label={<Text strong text='Eliminado cliente' />}
                                                value={
                                                    <Row>
                                                        { unpickedAmount !== 0 && <Text text='-' /> }
                                                        <Text text={<Number value={unpickedAmount} />} />
                                                    </Row>
                                                }
                                            />
                                            <DataField
                                                alignValues='flex-end'
                                                label={<Text strong text='Sub total' />}
                                                value={
                                                    <Row>
                                                        { subTotalAmount() < 0 && <Text text='-' /> }
                                                        <Text text={<Number value={subTotalAmount()} />} />
                                                    </Row>
                                                }
                                            />
                                            <Divider margin={5} />
                                            <DataField
                                                alignValues='flex-end'
                                                label={<Text strong text='Despacho' />}
                                                value={<ModifyPrice data={data} />}
                                            />
                                            <DataField
                                                alignValues='flex-end'
                                                label={<Text strong text='Total Picking' />}
                                                value={<Text strong text={<Number value={['34'].includes(status) ? 0 : amountInvoice} />} />}
                                            />
                                            <HasPermissions to={['order-detail-page:show-invoiced-amount']}>
                                                <DataField
                                                    alignValues='flex-end'
                                                    label={<Text strong text='Total Facturado' />}
                                                    value={<Text strong text={<Number value={['34'].includes(status) ? 0 : get(posDetail, 'amount', 0)} />} />}
                                                />
                                            </HasPermissions>
                                            <FormPOS data={data} />
                                        </ConditionalRender>
                                        <ConditionalRender condition={!withPicking}>
                                            <DataField
                                                alignValues='flex-end'
                                                label={<Text semibold text='Pickeados' />}
                                                value={<Text text={'-'} />}
                                            />
                                            <DataField
                                                alignValues='flex-end'
                                                label={<Text semibold text='Descuentos sustitutos' />}
                                                value={<Text text={'-'} />}
                                            />
                                            <DataField
                                                alignValues='flex-end'
                                                label={<Text semibold text='Exceso de sustitutos' />}
                                                value={<Text text={'-'} />}
                                            />
                                            <DataField
                                                alignValues='flex-end'
                                                label={<Text semibold text='Sin stock' />}
                                                value={<Text text={'-'} />}
                                            />
                                            <DataField
                                                alignValues='flex-end'
                                                label={<Text semibold text='Agregados' />}
                                                value={<Text text={'-'} />}
                                            />
                                            <DataField
                                                alignValues='flex-end'
                                                label={<Text semibold text='Eliminado cliente' />}
                                                value={<Text text={'-'} />}
                                            />
                                            <DataField
                                                alignValues='flex-end'
                                                label={<Text strong text='Sub total' />}
                                                value={<Text text={'-'} />}
                                            />
                                            <Divider margin={5} />
                                            <DataField
                                                alignValues='flex-end'
                                                label={<Text semibold text='Despacho' />}
                                                value={<ModifyPrice data={data} />}
                                            />
                                            <DataField
                                                alignValues='flex-end'
                                                label={<Text strong text='Total Picking' />}
                                                value={<Text strong text={'-'} />}
                                            />
                                            <HasPermissions to={['order-detail-page:show-invoiced-amount']}>
                                                <DataField
                                                    alignValues='flex-end'
                                                    label={<Text strong text='Total Facturado' />}
                                                    value={<Text strong text={'-'} />}
                                                />
                                            </HasPermissions>
                                            <FormPOS data={data} />
                                        </ConditionalRender>
                                    </MobileWrapper>
                                )}
                                footerChildren={() => (
                                    withPicking && 
                                    !pickedForInvoice && (
                                      <>
                                        <DownloadInvoice {...data} {...posDetail} />
                                        <DownloadChangeTicket {...data} {...posDetail} />
                                      </>
                                    )
                                  )}
                            />
                        </Box>
                        <Box width={['100%', '50%', '25%']} style={boxStyle}>
                            <Card
                                margin={7}
                                noHeaderDivider
                                leftHeader={() => {
                                    const subtitles = [
                                        `Modo de entrega | ${get(data, 'logisticsInfo.selectedSla')}`,
                                        `Fecha de descarga | ${moment(get(data, 'downloadedAt'), 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY HH:mm')}`
                                    ];
                                    return (
                                        <Row>
                                            <Column flex={2}>
                                                <Title text={store} />
                                                { map(subtitles, (subtitle, index) => (
                                                    <Subtitle
                                                        key={index}
                                                        color='#848484'
                                                        text={subtitle}
                                                    />
                                                ))}
                                                {get(data, 'extIssueDelayedByQueuing') > 0 && renderAlertQueuing(true)}
                                            </Column>
                                            <InfoToggler onClick={() => setShowDeliveryInfo(!showDeliveryInfo)} opened={!showDeliveryInfo} />
                                        </Row>
                                    );
                                }}
                                bodyChildren={() => (
                                    <MobileWrapper condition={!showDeliveryInfo} showDesktop>
                                        <CardDivider />
                                        <DataField
                                            label={<Text strong text='order_detail.created_at' />}
                                            value={<Text text={moment(get(data, 'createdAt'), 'YYYY-MM-DD HH:mm').format('DD/MM/YYYY HH:mm')} />}
                                        />
                                        <ReceiverInfo data={data} />
                                        <OrderShippingEstimate {...data}>
                                            {({ deliveryTime, deliveryTimeDate }) => (
                                                <>
                                                    <DataField
                                                        label={<Text strong text='order_detail.shipping_estimate_date' />}
                                                        value={
                                                            <Text
                                                                text={deliveryTimeDate}
                                                                strong={shippingChange}
                                                                color={shippingChange ? '#5665f5' : '#333333'}
                                                            />
                                                        }
                                                    />
                                                    <DataField
                                                        label={<Text strong text='order_detail.shipping_promised_date' />}
                                                        value={
                                                            <Row>
                                                                {!shippingChange && <If
                                                                    condition={get(data, 'windowAnticipated', false)}
                                                                    then={<Icon name='clock-fast' size={18} right={3} color={TIGERS_EYE} />}
                                                                />}
                                                                <Text text={getFormatDeliveryTime(deliveryTime, get(data, 'windowAnticipated', false))} strong={shippingChange} color={shippingChange ? '#5665f5' : '#333333'} />
                                                            </Row>
                                                        }
                                                    />
                                                    <DataField
                                                        label={<Text strong text='Transportadora' />}
                                                        value={<Text text={get(data, 'logisticsInfo.deliveryIds[0].courierName', '')} strong={shippingChange} color={shippingChange ? '#5665f5' : '#333333'} />}
                                                    />
                                                    {courierDelivery ? (
                                                        <DataField
                                                            label={<Text strong text='Entrega' />}
                                                            value={ <Text text={courierDelivery}strong={shippingChange} color={shippingChange ? '#5665f5' : '#333333'} />
                                                            }
                                                        />
                                                    ) : null}
                                                    {get(data, 'extIssueIsDelayed') && renderAlertQueuing(false)}
                                                </>
                                            )}
                                        </OrderShippingEstimate>
                                        {/* <Text as='div' size={14} text='Despacho' style={{ margin: 0, color: '#333333' }} strong />
                                        <Divider margin={5} /> */}
                                        {/* <DataField
                                            label={<Text strong text='Patente' />}
                                            value={<Text text='-' />}
                                        />
                                        <DataField
                                            label={<Text strong text='Conductor' />}
                                            value={<Text text='-' />}
                                        />
                                        <DataField
                                            label={<Text strong text='Empresa' />}
                                            value={<Text text='-' />}
                                        />
                                        <DataField
                                            label={<Text strong text='Ubicación' />}
                                            value={<Text text='-' />}
                                        /> */}
                                    </MobileWrapper>
                                )}
                            />
                        </Box>
                    </div>
                );
            }}
        </IsMobileWrapper>

    );
};

OrderResume.propTypes = {
    data: object,
    withPicking: bool,
    inPicking: bool,
    onClickCallHistory: func
};

OrderResume.defaultProps = {
    data: {},
    withPicking: false,
    inPicking: true,
    onClickCallHistory: () => {}
};

export default withApollo(withReloadable(OrderResume, ['ts']));